import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * DEV-015D · Web-side 30-minute inactivity watcher.
 *
 * Backend enforces the timeout authoritatively — this hook is only
 * responsible for:
 *   1. Detecting genuine user interaction (click, keydown, touchstart).
 *   2. Reporting activity to the backend, debounced.
 *   3. Showing a 2-minute warning at 28 minutes of idle.
 *   4. Signing the user out at 30 minutes.
 *
 * Passive events (silent refresh, network polling, visibility change,
 * `mousemove` inside a Playwright test) do NOT count as activity —
 * we deliberately omit `mousemove` from the listener set so simply
 * moving the pointer over the tab does not extend the session.
 *
 * `visibilitychange` from hidden → visible does NOT count either — the
 * user has to actually interact for the countdown to reset.
 */
const IDLE_MS = 30 * 60 * 1000;
const WARN_AT_MS = 28 * 60 * 1000;
const PING_DEBOUNCE_MS = 60 * 1000;
const TICK_MS = 1000;

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart"];

export function useIdleTimeout({ enabled, onLogout }) {
    const [warning, setWarning] = useState(false);
    const [remainingMs, setRemainingMs] = useState(IDLE_MS);
    const lastActivityRef = useRef(Date.now());
    const lastPingRef = useRef(0);

    const registerActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
        setWarning(false);
        setRemainingMs(IDLE_MS);
        const now = Date.now();
        if (now - lastPingRef.current >= PING_DEBOUNCE_MS) {
            lastPingRef.current = now;
            // Best-effort — network errors here MUST NOT propagate.
            // Backend is authoritative on the actual timeout.
            api.reportActivity().catch(() => { /* ignored */ });
        }
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        const handler = () => registerActivity();
        for (const ev of ACTIVITY_EVENTS) {
            window.addEventListener(ev, handler, { passive: true });
        }
        return () => {
            for (const ev of ACTIVITY_EVENTS) {
                window.removeEventListener(ev, handler);
            }
        };
    }, [enabled, registerActivity]);

    useEffect(() => {
        if (!enabled) return undefined;
        // Reset any stale timer state when the hook becomes active.
        lastActivityRef.current = Date.now();
        lastPingRef.current = 0;
        setWarning(false);
        setRemainingMs(IDLE_MS);

        const id = setInterval(() => {
            const idle = Date.now() - lastActivityRef.current;
            const remaining = Math.max(0, IDLE_MS - idle);
            setRemainingMs(remaining);
            if (idle >= IDLE_MS) {
                setWarning(false);
                try { onLogout && onLogout(); } catch { /* ignore */ }
                return;
            }
            if (idle >= WARN_AT_MS && !warning) {
                setWarning(true);
            }
        }, TICK_MS);
        return () => clearInterval(id);
    }, [enabled, onLogout, warning]);

    const stayActive = useCallback(() => {
        // The Stay-signed-in button counts as a deliberate interaction.
        // Force an immediate activity ping so the backend picks it up
        // without waiting for the debounce.
        lastActivityRef.current = Date.now();
        lastPingRef.current = Date.now();
        setWarning(false);
        setRemainingMs(IDLE_MS);
        api.reportActivity().catch(() => { /* ignore */ });
    }, []);

    return { warning, remainingMs, stayActive };
}
